import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import type { Child } from "@/types";
import { getChildrenList } from "@/api";
import { useTelegram } from "./useTelegram";
// import { fakeInitDataRaw } from "@/lib/auth";

const DEFAULT_PAGE_SIZE = 10;

export interface UseChildrenResult {
  children: Child[];
  /** Jami yozuvlar soni (API total — joriy filtr bo'yicha) */
  total: number;
  isLoading: boolean;
  isLoadingMore: boolean;
  error: Error | null;
  hasMore: boolean;
  loadMore: () => Promise<void>;
  refetch: () => Promise<void>;
  /** Ro'yxatdan ID bo'yicha bitta bola (getListdan) */
  findChildById: (id: number) => Child | null;
}

interface UseChildrenParams {
  /** undefined = barchasi, number = filtrlangan typeNeedId */
  typeNeedId?: number;
  /** Status filtri: default [3] (faol yig'uvlar) */
  statusIds?: number[];
}

/**
 * Bolalar ro'yxati uchun pagination hook.
 *
 * Har bir chaqiruv to'liq mustaqil state ushlaydi (page, rows, total).
 * App.tsx da bir nechta tab uchun parallel chaqiriladi — har biri o'ziga xos
 * statusIds/typeNeedId bilan, va bir-biriga ta'sir qilmaydi.
 *
 * Stale closure'larga qarshi `ref` pattern ishlatilgan: callback'larda eng
 * so'nggi state qiymatlari ref orqali o'qiladi. Stale API javoblar
 * `requestIdRef` orqali e'tiborsiz qoldiriladi.
 */
export function useChildren({ typeNeedId, statusIds }: UseChildrenParams = {}): UseChildrenResult {
  const [rawRows, setRawRows] = useState<Child[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const { initDataRaw, isReady } = useTelegram();
  const { i18n } = useTranslation();

  // ── Refs: callback'larda eng so'nggi qiymatlarni o'qish uchun ──
  // Bu yerda hech qachon stale closure bo'lmaydi: ref har render dan keyin
  // sinxronlanadi va loadMore ichidan to'g'ridan-to'g'ri o'qiladi.
  const pageRef = useRef(1);
  const rowsRef = useRef<Child[]>([]);
  const totalRef = useRef(0);
  const isLoadingMoreRef = useRef(false);
  // Stale response'ni e'tiborsiz qoldirish uchun: har bir yangi fetch
  // o'z ID si bilan boshlanadi; javob kelganda ID hali ham aktualligini tekshiramiz
  const requestIdRef = useRef(0);

  useEffect(() => { pageRef.current = page; }, [page]);
  useEffect(() => { rowsRef.current = rawRows; }, [rawRows]);
  useEffect(() => { totalRef.current = total; }, [total]);
  useEffect(() => { isLoadingMoreRef.current = isLoadingMore; }, [isLoadingMore]);

  // Array reference o'zgarib turishidan qutulish uchun stable string key
  const statusIdsKey = useMemo(
    () => (statusIds ? statusIds.slice().sort().join(",") : ""),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [statusIds?.join(",")]
  );

  // ── Initial fetch va parametr o'zgarganda qayta yuklash ──
  useEffect(() => {
    if (!isReady) return;
    const myRequestId = ++requestIdRef.current;

    setPage(1);
    pageRef.current = 1;
    setIsLoading(true);
    setError(null);
    setRawRows([]);
    rowsRef.current = [];

    getChildrenList({
      page: 1,
      pageSize: DEFAULT_PAGE_SIZE,
      initData: initDataRaw,
      typeNeedId,
      statusIds,
      // initData: fakeInitDataRaw,
    })
      .then(({ rows, total: t }) => {
        // Eski request bo'lsa — natijani tashlab yuboramiz
        if (myRequestId !== requestIdRef.current) return;
        setRawRows(rows);
        setTotal(t);
      })
      .catch((e) => {
        if (myRequestId !== requestIdRef.current) return;
        setError(e instanceof Error ? e : new Error(String(e)));
        setRawRows([]);
      })
      .finally(() => {
        if (myRequestId !== requestIdRef.current) return;
        setIsLoading(false);
      });
    // statusIdsKey — stable string, array reference emas
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initDataRaw, statusIdsKey, typeNeedId, isReady, i18n.language]);

  // ── Keyingi sahifani yuklash ──
  // `pageRef.current` orqali EHTIROZSIZ joriy page o'qiladi.
  // Hech qanday "page" state callback deps ida YO'Q — bu stale closure'larni
  // butunlay yo'q qiladi.
  const loadMore = useCallback(async () => {
    if (isLoadingMoreRef.current) return;
    if (rowsRef.current.length >= totalRef.current) return;

    const myRequestId = ++requestIdRef.current;
    const nextPage = pageRef.current + 1;
    setIsLoadingMore(true);

    try {
      const { rows, total: t } = await getChildrenList({
        page: nextPage,
        pageSize: DEFAULT_PAGE_SIZE,
        initData: initDataRaw,
        typeNeedId,
        statusIds,
      });
      // Eski request bo'lsa, javobni e'tiborsiz qoldiramiz
      if (myRequestId !== requestIdRef.current) return;
      setTotal(t);
      setRawRows((prev) => {
        const seen = new Set(prev.map((c) => c.id));
        const appended = rows.filter((r) => !seen.has(r.id));
        return [...prev, ...appended];
      });
      setPage(nextPage);
      pageRef.current = nextPage;
    } catch (e) {
      if (myRequestId !== requestIdRef.current) return;
      setError(e instanceof Error ? e : new Error(String(e)));
    } finally {
      if (myRequestId === requestIdRef.current) {
        setIsLoadingMore(false);
      }
    }
    // statusIdsKey stable string deps; statusIds esa closure dan o'qiladi
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initDataRaw, typeNeedId, statusIdsKey]);

  // ── Birinchi sahifani qayta yuklash ──
  const refetch = useCallback(async () => {
    const myRequestId = ++requestIdRef.current;
    setPage(1);
    pageRef.current = 1;
    setIsLoading(true);
    setError(null);

    try {
      const { rows, total: t } = await getChildrenList({
        page: 1,
        pageSize: DEFAULT_PAGE_SIZE,
        initData: initDataRaw,
        typeNeedId,
        statusIds,
      });
      if (myRequestId !== requestIdRef.current) return;
      setRawRows(rows);
      setTotal(t);
    } catch (e) {
      if (myRequestId !== requestIdRef.current) return;
      setError(e instanceof Error ? e : new Error(String(e)));
      setRawRows([]);
    } finally {
      if (myRequestId === requestIdRef.current) {
        setIsLoading(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initDataRaw, typeNeedId, statusIdsKey]);

  const findChildById = useCallback(
    (id: number) => rawRows.find((c) => c.id === id) ?? null,
    [rawRows]
  );

  const hasMore = total > rawRows.length;

  return {
    children: rawRows,
    total,
    isLoading,
    isLoadingMore,
    error,
    hasMore,
    loadMore,
    refetch,
    findChildById,
  };
}
